<template>
	<div class="flex gap-2 pb-4">
		<CopyButton
			v-for="button in buttonNames"
			:button-index="button.buttonIndex"
			:field-or-section="fieldOrSection"
			:key="button.buttonIndex"
			:name="button.name"
			@copy="handleCopy"
			type="button"
		/>
	</div>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import CopyButton from '@/components/presentation/CopyButton.vue'
import { usePresentationLocale } from '@/i18n'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { computed, toRaw } from 'vue'

const { t } = usePresentationLocale()

const props = defineProps({
	fieldOrSection: {
		type: [String, Array] as unknown as () => keyof Plan | Array<keyof Plan>,
		required: false,
	},
	activeIndex: {
		type: Number,
		required: true,
	},
})

const emits = defineEmits(['click'])
const session = useSessionStore()
const plansStore = usePlanStore()

/**
 * Computes an array of objects containing the name and button index for each plan except the active one.
 * The name is either the plan's first name in uppercase or "PERSON X" where X is the person count if the plan has no name.
 * @returns {Array<Object<string, number>>} An array of objects with name and buttonIndex properties.
 */
const buttonNames = computed(() => {
	const names: { name: string; buttonIndex: number }[] = []
	const activeIndex = props.activeIndex

	if (!session.activePlans) return names

	let personCount = 1

	session.activePlans.forEach((plan, buttonIndex) => {
		if (buttonIndex !== activeIndex) {
			let name: string

			if (plan.firstName) {
				name = plan.firstName.toUpperCase()
			} else {
				name = `${t('copyButton.person').toUpperCase()} ${personCount++}`
			}

			names.push({ name, buttonIndex })
		}
	})
	return names
})

/**
 * Handles the copy functionality for a specific button index.
 * @param {number} buttonIndex - The index of the button to handle the copy for.
 */
function handleCopy(buttonIndex: number) {
	emits('click', buttonIndex, props.activeIndex)

	// Some uses just want the emit
	if (!props.fieldOrSection) return

	if (props.fieldOrSection && session.activePlans) {
		const sourcePlan = session.activePlans[buttonIndex]
		const targetPlan = session.activePlans[props.activeIndex]

		if (!sourcePlan) throw new Error('Source plan not found')

		if (!targetPlan) throw new Error('Target plan not found')

		// Copy the specific field/section from source to target
		plansStore.copyProperties(sourcePlan, targetPlan, props.fieldOrSection)
	}
}
</script>
